import { PlusIcon } from "@heroicons/react/24/outline";
import {
    activityCategories,
    ActivityCategory,
    activityCategoryLabels,
    ActivityRecordType,
    activityRecordTypeLabels,
    asActivityCategory,
    asActivityRecordTypes,
} from "@sp-crm/core";
import { useQuery } from "@tanstack/react-query";
import { QueryRenderer } from "components/clients/show-client/community-comparison/query-renderer";
import { ContentFull, ContentHeader } from "components/layout";
import { trpc } from "components/rpc/trpc";
import { activityCategoryIcons } from "components/tasks/activity-category-icons";
import { DeleteButton, EditButton, RestoreButton } from "components/ui/action-button";
import { ButtonSelect, ButtonSelectOption } from "components/ui/button-select";
import { fancyConfirm } from "components/ui/fancy-confirm";
import { Input } from "components/ui/input";
import { defaultLinkStyle } from "components/ui/link";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryButton } from "components/ui/secondary-button";
import { Spinner } from "components/ui/spinner";
import { Formik, FormikHelpers } from "formik";
import {
    CreateTaskTypePayload,
    GetTaskTypesQuery,
    UpdateTaskTypePayload,
    useCreateTaskTypeMutation,
    useGetTaskTypesQuery,
    useUpdateTaskTypeMutation,
} from "generated/graphql";
import { useTaskNouns } from "hooks/use-task-nouns";
import React, { useCallback } from "react";
import { useQueryClient } from "react-query";
import { useCrmMode } from "store/selectors/hooks";
import {
    homeCareClientsSettingsLabel,
    placementClientsSettingsLabel,
    recordTypesForCrmMode,
} from "util/crm-mode";
import { AgencyTogglePref } from "./agency-settings-toggle";
import {
    recordTypeSummary,
    RecordTypeUsage,
    removalConfirmation,
    removedRecordTypes,
} from "./record-type-usage";

type TaskTypeRecord = GetTaskTypesQuery["getTaskTypes"][0];

const categoryForTaskType = (taskType: TaskTypeRecord): ActivityCategory =>
    asActivityCategory(taskType.category);

const recordTypesForTaskType = (taskType: TaskTypeRecord): ActivityRecordType[] =>
    asActivityRecordTypes(taskType.recordTypes);

const byName = (a: TaskTypeRecord, b: TaskTypeRecord): number =>
    a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase());

const taskTypeDisplayName = (taskType: TaskTypeRecord): string =>
    taskType.name.length > 0 ? taskType.name : "(unnamed)";

const useInvalidateTaskTypes = (): (() => void) => {
    const queryClient = useQueryClient();
    return useCallback(() => {
        void queryClient.invalidateQueries("getTaskTypes");
    }, [queryClient]);
};

export const AgencySettingsTasks: React.FC<unknown> = () => {
    const nouns = useTaskNouns();
    const taskTypeQuery = useGetTaskTypesQuery();
    const availableRecordTypes = useAvailableRecordTypes();
    return (
        <>
            <ContentFull>
                <ContentHeader>{`${nouns.titleSingular} Settings`}</ContentHeader>
                <AgencyTogglePref
                    field="alertClientsWithNoTasks"
                    label={`Require active ${nouns.plural} for every client`}
                    description={`When enabled, you will be alerted when trying to leave a client that has no active ${nouns.plural}. This can help ensure every client is receiving the attention they need.`}
                />
            </ContentFull>
            <ContentFull className="mt-4">
                <ContentHeader>Activity Types</ContentHeader>
                <p className="mb-4 max-w-xl text-sm text-gray-500">
                    Configure the activity types your team uses to do their work. Every
                    type belongs to a category, which determines how completed activities
                    are tracked, and applies to one or more record types.
                </p>
                <QueryRenderer query={taskTypeQuery} name="Agency Settings Tasks">
                    {data => {
                        const taskTypes = data.getTaskTypes.filter(taskType =>
                            recordTypesForTaskType(taskType).some(recordType =>
                                availableRecordTypes.includes(recordType),
                            ),
                        );
                        const archivedTaskTypes = taskTypes
                            .filter(taskType => taskType.isArchived)
                            .sort(byName);
                        const activeTaskTypes = taskTypes
                            .filter(taskType => !taskType.isArchived)
                            .sort(byName);
                        return (
                            <div className="max-w-xl space-y-4">
                                {activityCategories.map(category => (
                                    <CategorySection
                                        key={category}
                                        category={category}
                                        taskTypes={activeTaskTypes.filter(
                                            taskType =>
                                                categoryForTaskType(taskType) ===
                                                category,
                                        )}
                                    />
                                ))}
                                {archivedTaskTypes.length > 0 ? (
                                    <div className="rounded bg-gray-50 p-4">
                                        <h2 className="text-lg twoverride font-bold">
                                            Archived
                                        </h2>
                                        <p className="mb-1 text-xs text-gray-500">
                                            {`Archived types cannot be used for new work, but existing activities and ${nouns.plural} keep them. Restoring returns a type to its category.`}
                                        </p>
                                        <ul className="divide-y divide-gray-200">
                                            {archivedTaskTypes.map(taskType => (
                                                <li key={taskType.id} className="py-2">
                                                    <TaskTypeRow taskType={taskType} />
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                ) : null}
                            </div>
                        );
                    }}
                </QueryRenderer>
            </ContentFull>
        </>
    );
};

interface CategorySectionProps {
    category: ActivityCategory;
    taskTypes: TaskTypeRecord[];
}

const CategorySection: React.FC<CategorySectionProps> = props => {
    const { category, taskTypes } = props;
    const [isAdding, setIsAdding] = React.useState(false);
    const startAdding = useCallback(() => setIsAdding(true), []);
    const stopAdding = useCallback(() => setIsAdding(false), []);
    const CategoryIcon = activityCategoryIcons[category];
    return (
        <section className="rounded-lg border border-gray-200">
            <div className="flex items-center gap-2 rounded-t-lg border-b border-gray-200 bg-gray-50 px-3 py-2.5">
                <CategoryIcon className="h-5 w-5 text-gray-600" />
                <h3 className="text-sm font-semibold text-gray-900">
                    {activityCategoryLabels[category]}
                </h3>
            </div>
            <div className="px-3">
                <ul className="divide-y divide-gray-200">
                    {taskTypes.map(taskType => (
                        <li key={taskType.id} className="py-2">
                            <TaskTypeRow taskType={taskType} />
                        </li>
                    ))}
                    <li className="py-2">
                        {isAdding ? (
                            <TaskTypeRowNew category={category} close={stopAdding} />
                        ) : (
                            <button
                                type="button"
                                onClick={startAdding}
                                className={defaultLinkStyle}>
                                <div className="flex items-center space-x-1">
                                    <PlusIcon className="w-5 h-5" />
                                    <span className="text-sm">
                                        Add {activityCategoryLabels[category]} type
                                    </span>
                                </div>
                            </button>
                        )}
                    </li>
                </ul>
            </div>
        </section>
    );
};

interface TaskTypeRowProps {
    taskType: TaskTypeRecord;
}

interface TaskTypeEdit {
    name: string;
    recordTypes: ActivityRecordType[];
}

const useAvailableRecordTypes = (): ActivityRecordType[] => {
    const crmMode = useCrmMode();
    return recordTypesForCrmMode(crmMode);
};

const useRecordTypeLabels = (): Record<ActivityRecordType, string> => {
    const { mode } = useCrmMode();
    return {
        ...activityRecordTypeLabels,
        placement: placementClientsSettingsLabel(mode),
        homeCare: homeCareClientsSettingsLabel(mode),
    };
};

interface RecordTypeSelectProps {
    selected: ActivityRecordType[];
    onChange: (recordTypes: ActivityRecordType[]) => void;
}

const RecordTypeSelect: React.FC<RecordTypeSelectProps> = props => {
    const { selected, onChange } = props;
    const availableRecordTypes = useAvailableRecordTypes();
    const labels = useRecordTypeLabels();
    const options: ButtonSelectOption[] = availableRecordTypes.map(recordType => ({
        id: recordType,
        label: labels[recordType],
    }));
    const toggle = (id: string) => {
        const recordType = asActivityRecordTypes([id])[0];
        onChange(
            selected.includes(recordType)
                ? selected.filter(existing => existing !== recordType)
                : [...selected, recordType],
        );
    };
    return (
        <div>
            <div className="text-xs font-medium text-gray-600">Applies to</div>
            <ButtonSelect options={options} selectedIds={selected} onToggle={toggle} />
        </div>
    );
};

interface RecordTypeSummaryProps {
    recordTypes: ActivityRecordType[];
}

const RecordTypeSummary: React.FC<RecordTypeSummaryProps> = props => {
    const { recordTypes } = props;
    const availableRecordTypes = useAvailableRecordTypes();
    const labels = useRecordTypeLabels();
    return (
        <p className="text-xs text-gray-500">
            {recordTypeSummary(recordTypes, availableRecordTypes, labels)}
        </p>
    );
};

interface RecordTypeUsageState {
    usage: RecordTypeUsage;
    isPending: boolean;
}

const useRecordTypeUsage = (taskTypeId: TaskTypeRecord["id"]): RecordTypeUsageState => {
    const trpcClient = trpc.useTRPC();
    const usageQuery = useQuery(
        trpcClient.taskType.recordTypeUsage.queryOptions({ taskTypeId }),
    );
    return {
        usage: usageQuery.data
            ? { kind: "known", openTaskCounts: usageQuery.data.openTaskCounts }
            : { kind: "unknown" },
        isPending: usageQuery.isPending,
    };
};

interface TaskTypeEditFormProps {
    taskType: TaskTypeRecord;
    currentRecordTypes: ActivityRecordType[];
    isPending: boolean;
    onSave: (values: TaskTypeEdit) => Promise<void>;
    onCancel: () => void;
}

const TaskTypeEditForm: React.FC<TaskTypeEditFormProps> = props => {
    const { taskType, currentRecordTypes, isPending, onSave, onCancel } = props;
    const { usage, isPending: isUsagePending } = useRecordTypeUsage(taskType.id);
    const labels = useRecordTypeLabels();
    const nouns = useTaskNouns();
    const submit = React.useCallback(
        async (values: TaskTypeEdit) => {
            const removed = removedRecordTypes(currentRecordTypes, values.recordTypes);
            const confirmation = removalConfirmation(
                taskTypeDisplayName(taskType),
                removed,
                usage,
                labels,
                nouns,
            );
            if (confirmation !== null) {
                const confirmed = await fancyConfirm(
                    confirmation.title,
                    confirmation.message,
                    "Yes, save",
                    "Cancel",
                    { confirmStyle: "question" },
                );
                if (!confirmed) {
                    return;
                }
            }
            await onSave(values);
        },
        [currentRecordTypes, labels, onSave, taskType, usage, nouns],
    );
    return (
        <Formik
            initialValues={{ name: taskType.name, recordTypes: currentRecordTypes }}
            onSubmit={submit}>
            {formValues => (
                <form onSubmit={formValues.handleSubmit} className="space-y-2">
                    <div className="flex items-end space-x-1">
                        <Input
                            type="text"
                            label="Name"
                            name="name"
                            onChange={formValues.handleChange}
                            onBlur={formValues.handleBlur}
                            value={formValues.values.name}
                        />
                        {isPending || isUsagePending ? (
                            <div className="w-7 h-7">
                                <Spinner />
                            </div>
                        ) : (
                            <>
                                <PrimaryButton
                                    disabled={
                                        formValues.values.name.trim().length === 0 ||
                                        formValues.values.recordTypes.length === 0
                                    }
                                    type="submit">
                                    Save
                                </PrimaryButton>
                                <SecondaryButton type="button" onClick={onCancel}>
                                    Cancel
                                </SecondaryButton>
                            </>
                        )}
                    </div>
                    <RecordTypeSelect
                        selected={formValues.values.recordTypes}
                        onChange={recordTypes =>
                            formValues.setFieldValue("recordTypes", recordTypes)
                        }
                    />
                </form>
            )}
        </Formik>
    );
};

export const TaskTypeRow: React.FC<TaskTypeRowProps> = props => {
    const { taskType } = props;
    const nouns = useTaskNouns();
    const [isPending, setIsPending] = React.useState(false);
    const [isEditing, setIsEditing] = React.useState(false);
    const invalidateTaskTypes = useInvalidateTaskTypes();
    const updateTaskType = useUpdateTaskTypeMutation({
        onSuccess: invalidateTaskTypes,
    });
    const archive = useCallback(async () => {
        const confirmed = await fancyConfirm(
            `Archive ${taskTypeDisplayName(taskType)}?`,
            `Activities and ${nouns.plural} already assigned this type will keep it. It can no longer be selected for new work, and you can restore it from the Archived section at any time.`,
            "Yes, archive",
            "Cancel",
            { confirmStyle: "question" },
        );
        if (!confirmed) {
            return;
        }
        setIsPending(true);
        const payload: UpdateTaskTypePayload = {
            isArchived: true,
            taskTypeId: taskType.id,
            name: taskType.name,
        };
        await updateTaskType.mutateAsync({ payload });
        setIsPending(false);
    }, [taskType, updateTaskType, nouns]);
    const restore = useCallback(async () => {
        setIsPending(true);
        const payload: UpdateTaskTypePayload = {
            isArchived: false,
            taskTypeId: taskType.id,
            name: taskType.name,
        };
        await updateTaskType.mutateAsync({ payload });
        setIsPending(false);
    }, [taskType, updateTaskType]);
    const enableEditing = useCallback(() => {
        setIsEditing(true);
    }, []);
    const cancelEditing = useCallback(() => {
        setIsEditing(false);
    }, []);
    const currentRecordTypes = recordTypesForTaskType(taskType);
    const save = React.useCallback(
        async (values: TaskTypeEdit) => {
            setIsPending(true);
            const payload: UpdateTaskTypePayload = {
                isArchived: taskType.isArchived,
                taskTypeId: taskType.id,
                name: values.name,
                recordTypes: values.recordTypes,
            };
            await updateTaskType.mutateAsync({ payload });
            setIsEditing(false);
            setIsPending(false);
        },
        [taskType, updateTaskType],
    );
    if (isEditing) {
        return (
            <TaskTypeEditForm
                taskType={taskType}
                currentRecordTypes={currentRecordTypes}
                isPending={isPending}
                onSave={save}
                onCancel={cancelEditing}
            />
        );
    }
    return (
        <div className="flex items-center justify-between gap-2">
            <div className="min-w-0 space-y-0.5">
                <div className="flex min-w-0 items-center gap-2">
                    <div className="truncate">{taskTypeDisplayName(taskType)}</div>
                    {taskType.isArchived ? (
                        <span className="inline-flex shrink-0 items-center rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-600">
                            {activityCategoryLabels[categoryForTaskType(taskType)]}
                        </span>
                    ) : null}
                </div>
                <RecordTypeSummary recordTypes={currentRecordTypes} />
            </div>
            <div className="flex shrink-0 items-center space-x-2">
                <EditButton onClick={enableEditing} backgroundColor={"bg-white"} />
                {isPending ? (
                    <div className="w-7 h-7">
                        <Spinner />
                    </div>
                ) : taskType.isArchived ? (
                    <RestoreButton onClick={restore} backgroundColor={"bg-white"} />
                ) : (
                    <DeleteButton onClick={archive} backgroundColor={"bg-white"} />
                )}
            </div>
        </div>
    );
};

interface TaskTypeRowNewProps {
    category: ActivityCategory;
    close: () => void;
}

export const TaskTypeRowNew: React.FC<TaskTypeRowNewProps> = props => {
    const { category, close } = props;
    const invalidateTaskTypes = useInvalidateTaskTypes();
    const initialRecordTypes = useAvailableRecordTypes();
    const createTaskType = useCreateTaskTypeMutation({
        onSuccess: invalidateTaskTypes,
    });
    const save = React.useCallback(
        async (values: TaskTypeEdit, formik: FormikHelpers<TaskTypeEdit>) => {
            const payload: CreateTaskTypePayload = {
                name: values.name,
                category,
                recordTypes: values.recordTypes,
            };
            await createTaskType.mutateAsync({ payload });
            formik.resetForm();
            close();
        },
        [createTaskType, category, close],
    );
    return (
        <Formik
            initialValues={{ name: "", recordTypes: initialRecordTypes }}
            onSubmit={save}>
            {formValues => (
                <form onSubmit={formValues.handleSubmit} className="space-y-2">
                    <div className="flex items-end space-x-1">
                        <Input
                            type="text"
                            label={`New ${activityCategoryLabels[category]} type`}
                            name="name"
                            autoFocus
                            onChange={formValues.handleChange}
                            onBlur={formValues.handleBlur}
                            value={formValues.values.name}
                        />
                        <PrimaryButton
                            disabled={
                                formValues.isSubmitting ||
                                formValues.values.name.trim().length === 0 ||
                                formValues.values.recordTypes.length === 0
                            }
                            type="submit">
                            Create
                        </PrimaryButton>
                        <SecondaryButton type="button" onClick={close}>
                            Cancel
                        </SecondaryButton>
                    </div>
                    <RecordTypeSelect
                        selected={formValues.values.recordTypes}
                        onChange={recordTypes =>
                            formValues.setFieldValue("recordTypes", recordTypes)
                        }
                    />
                </form>
            )}
        </Formik>
    );
};
